import { memo, useCallback, useMemo } from 'react';

import { type BaseCustomField } from '@air/api/types';
import { useBatchUpdateFormAssetsMutation } from '@air/feature-content-collection/hooks';
import {
  type ContentCollectionCustomField,
  contentCollectionSessionIdSelector,
} from '@air/feature-content-collection/redux';

import { ContentCollectionBaseCustomFieldPlainText } from '~/components/ContentCollection/ContentCollectionBaseCustomFieldPlainText';
import { type ContentCollectionBaseCustomFieldsProps } from '~/components/ContentCollection/ContentCollectionBaseCustomFieldsSelect';
import { useAirStore } from '~/utils/ReduxUtils';

export type ContentCollectionCustomFieldPlainTextProps = {
  customField: BaseCustomField;
  onSuccess?: (params: { customFieldWithValues: ContentCollectionCustomField; value: string | undefined }) => void;
  value: string | undefined;
} & Omit<ContentCollectionBaseCustomFieldsProps, 'field'>;

export const ContentCollectionCustomFieldPlainText = memo(
  ({ clips, customField, disabled, onSuccess, value, showDescription }: ContentCollectionCustomFieldPlainTextProps) => {
    const store = useAirStore();
    const { mutate: batchUpdateFormAssets, isPending } = useBatchUpdateFormAssetsMutation();

    const customFieldWithValues = useMemo(
      () => ({
        ...customField,
        value: null,
        values: null,
        plainTextValue: value,
      }),
      [customField, value],
    );

    const onTextChange = useCallback(
      (value: string) => {
        const sessionId = contentCollectionSessionIdSelector(store.getState());

        if (!sessionId) {
          throw new Error('No session id');
        }

        batchUpdateFormAssets(
          {
            assetIds: clips.map(({ assetId }) => assetId),
            formSessionId: sessionId,
            update: {
              customFields: [{ customFieldId: customField.id, plainTextValue: value }],
            },
          },
          {
            onSuccess: () => {
              onSuccess?.({ customFieldWithValues, value });
            },
          },
        );
      },
      [clips, batchUpdateFormAssets, customField.id, customFieldWithValues, onSuccess, store],
    );

    return (
      <ContentCollectionBaseCustomFieldPlainText
        disabled={disabled || isPending}
        clips={clips}
        field={customFieldWithValues}
        onTextChange={onTextChange}
        showDescription={showDescription}
      />
    );
  },
);

ContentCollectionCustomFieldPlainText.displayName = 'ContentCollectionCustomFieldPlainText';
